import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { SelectType, DisplayAlignment, DisplaySize, ImageStyle, PEInfoType, normalizePEInfoType, usesWorkDuration } from '../../shared/enum/EnumRepository';
import { PEWorkData } from '../../shared/dto/EntityRepository';
import SelectBox from '../../shared/component/SelectBox';
import { useImageStyleJudge } from '../../shared/hooks/useImageStyleJudge';
import WorkInfo from './WorkInfo';
import WorkInfoLeft from './WorkInfoLeft';
import WorkInfoRight from './WorkInfoRight';
import WorkImgInPe from './WorkImgInPe';
import WorkVideoInPe from './WorkVideoInPe';
import { PEIndicator } from './PEIndicator';

export { PEIndicator };

export interface WorkProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  infoType?: PEInfoType;
  data: PEWorkData;
  priority: boolean;
}

const Work: React.FC<WorkProps> = ({ peId, alignment, displaySize, infoType, data, priority }) => {
  const { isEditMode } = useEditMode();
  const handleDisplayAlignmentChange = useProjectElementListStore((s) => s.updateDisplayAlignment);
  const handleDisplaySizeChange = useProjectElementListStore((s) => s.updateDisplaySize);
  const handleInfoTypeChange = useProjectElementListStore((s) => s.updateInfoType);
  const { judgeImageStyle } = useImageStyleJudge();
  const imageStyle = useMemo(() => judgeImageStyle(data.uploadFile?.width / data.uploadFile?.height), [data.uploadFile?.width, data.uploadFile?.height]);
  const effectiveInfoType = normalizePEInfoType(infoType);

  return (
    <PEImgContentWrapper>
      {isEditMode &&
        <PEHeader>
          <SelectBoxContainer>
            <SelectBoxWrapper>
              <SelectBox
                value={displaySize || DisplaySize.BIG}
                selectType={SelectType.WORK_SIZE}
                handleChange={(value) => handleDisplaySizeChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
            <SelectBoxWrapper>
              <SelectBox
                value={alignment || DisplayAlignment.CENTER}
                selectType={SelectType.DISPLAY_ALIGNMENT}
                handleChange={value => handleDisplayAlignmentChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
            <SelectBoxWrapper>
              <SelectBox
                value={effectiveInfoType}
                selectType={SelectType.PE_INFO_TYPE}
                handleChange={(value) => handleInfoTypeChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
          </SelectBoxContainer>
          <PEIndicator peId={peId}>: Work</PEIndicator>
        </PEHeader>
      }
      <ImgContentCoreWrapper>
        <ImgWrapper>
          <WorkInfoLeft
            peId={peId}
            alignment={alignment || DisplayAlignment.CENTER}
            displaySize={displaySize || DisplaySize.BIG}
            imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            infoType={effectiveInfoType}
            data={data} />
          {usesWorkDuration(data.workType) ? (
            <WorkVideoInPe
              peId={peId}
              alignment={alignment || DisplayAlignment.CENTER}
              displaySize={displaySize || DisplaySize.BIG}
              data={data}
              priority={priority}
              imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            />
          ) : (
            <WorkImgInPe
              peId={peId}
              alignment={alignment || DisplayAlignment.CENTER}
              displaySize={displaySize || DisplaySize.BIG}
              data={data}
              priority={priority}
              imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            />
          )}
          <WorkInfoRight
            peId={peId}
            alignment={alignment || DisplayAlignment.CENTER}
            displaySize={displaySize || DisplaySize.BIG}
            imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            infoType={effectiveInfoType}
            data={data} />
        </ImgWrapper>
        <WorkInfo
          peId={peId}
          alignment={alignment || DisplayAlignment.CENTER}
          displaySize={displaySize || DisplaySize.BIG}
          imageStyle={imageStyle || ImageStyle.LANDSCAPE}
          infoType={effectiveInfoType}
          data={data} />
      </ImgContentCoreWrapper>
    </PEImgContentWrapper>
  );
};

export const PEImgContentWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const PEHeader = styled.div`
  position: absolute;
  top: -24px;
  width: 100%;

  padding: 0 calc(8vw);
  display: flex;
  justify-content: space-between;
`;

export const SelectBoxContainer = styled.div`
  width: 100%;
  display: flex;
  gap: clamp(12px, 1vw, 14px);
`;

export const SelectBoxWrapper = styled.article`
  width: clamp(70px, 23vw, 90px);
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

export const ImgContentCoreWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const ImgWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

export default Work;
